import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import i18n from '../i18n';

export interface GoogleAdsAccount {
  id: number;
  account_name: string;
  account_id: string;
  currency: string;
  sync_enabled: boolean;
  sync_status: string;
  sync_error: string | null;
  last_synced_at: string | null;
  is_mcc: boolean;
  mcc_account_id: string | null;
}

export interface SyncProgress { percent: number; label: string; }

interface GoogleAdsAccountContextType {
  accounts: GoogleAdsAccount[];
  activeAccountId: number | null;
  activeAccount: GoogleAdsAccount | null;
  setActiveAccountId: (id: number | null) => void;
  loading: boolean;
  syncProgress: Record<number, SyncProgress>;
  refreshAccounts: () => Promise<void>;
  triggerSync: (accountId: number) => Promise<void>;
  toggleSyncEnabled: (accountId: number) => Promise<void>;
}

const GoogleAdsAccountContext = createContext<GoogleAdsAccountContextType | null>(null);

export function useGoogleAdsAccount(): GoogleAdsAccountContextType {
  const ctx = useContext(GoogleAdsAccountContext);
  if (!ctx) throw new Error('useGoogleAdsAccount must be used within GoogleAdsAccountProvider');
  return ctx;
}

export const GoogleAdsAccountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accounts, setAccounts] = useState<GoogleAdsAccount[]>([]);
  const [activeAccountId, setActiveAccountIdState] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem('google_ads_active_account');
      return saved ? parseInt(saved) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [syncProgress, setSyncProgress] = useState<Record<number, SyncProgress>>({});

  const refreshAccounts = useCallback(async () => {
    try {
      const res = await api.getGoogleAdsAccounts();
      const list: GoogleAdsAccount[] = (res.data?.accounts || []).map((a: any) => ({
        id: a.id,
        account_name: a.account_name || i18n.t('Hesap {{id}}', { id: a.account_id }),
        account_id: a.account_id,
        currency: a.currency || 'TRY',
        sync_enabled: !!a.sync_enabled,
        sync_status: a.sync_status || 'idle',
        sync_error: a.sync_error || null,
        last_synced_at: a.last_synced_at || null,
        is_mcc: !!a.is_mcc,
        mcc_account_id: a.mcc_account_id || null,
      }));
      setAccounts(list);

      // Validate stored selection
      const stored = activeAccountId;
      if (stored !== null && !list.some(a => a.id === stored)) {
        setActiveAccountIdState(null);
        localStorage.removeItem('google_ads_active_account');
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [activeAccountId]);

  useEffect(() => { refreshAccounts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll sync status while any account is syncing; the interval self-stops once none remain.
  // Depend on the boolean (not the accounts array) so each poll's setAccounts doesn't tear
  // down and permanently kill the interval.
  const hasSyncing = accounts.some(a => a.sync_status === 'syncing');
  useEffect(() => {
    if (!hasSyncing) return;
    const iv = setInterval(() => { refreshAccounts(); }, 8000);
    return () => clearInterval(iv);
  }, [hasSyncing, refreshAccounts]);

  // Poll live per-account progress (percent + step label) while syncing so the header can show
  // real progress, not just a spinner. Lightweight endpoint — poll faster than the status refresh.
  useEffect(() => {
    if (!hasSyncing) { setSyncProgress({}); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.getGoogleAdsSyncProgress();
        if (cancelled) return;
        const map: Record<number, SyncProgress> = {};
        for (const a of (res?.progress?.active || [])) {
          map[a.accountId] = { percent: a.percent ?? 0, label: a.label || '' };
        }
        setSyncProgress(map);
      } catch { /* transient */ }
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [hasSyncing]);

  const setActiveAccountId = useCallback((id: number | null) => {
    setActiveAccountIdState(id);
    if (id !== null) {
      localStorage.setItem('google_ads_active_account', String(id));
    } else {
      localStorage.removeItem('google_ads_active_account');
    }
  }, []);

  const activeAccount = accounts.find(a => a.id === activeAccountId) || null;

  const triggerSync = useCallback(async (accountId: number) => {
    try {
      await api.post(`/google-ads/sync/${accountId}`);
      // Optimistic update
      setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, sync_status: 'syncing' } : a));
    } catch {}
  }, []);

  const toggleSyncEnabled = useCallback(async (accountId: number) => {
    try {
      await api.put(`/google-ads/accounts/${accountId}/sync-toggle`);
      setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, sync_enabled: !a.sync_enabled } : a));
    } catch {}
  }, []);

  return (
    <GoogleAdsAccountContext.Provider value={{
      accounts, activeAccountId, activeAccount, setActiveAccountId,
      loading, syncProgress, refreshAccounts, triggerSync, toggleSyncEnabled,
    }}>
      {children}
    </GoogleAdsAccountContext.Provider>
  );
};
